import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, ClipboardList, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "../../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function TherapistDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [p, u] = await Promise.all([
        axios.get(`${API}/patients`),
        axios.get(`${API}/patients/unassigned`),
      ]);
      setPatients(p.data);
      setUnassigned(u.data);
    } catch {
      toast.error("Patienten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const claim = async (patientId) => {
    try {
      await axios.post(`${API}/patients/${patientId}/claim`);
      toast.success("Patient übernommen");
      load();
    } catch {
      toast.error("Übernahme fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="therapist-dashboard-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-800 border-t-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="therapist-dashboard">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-zinc-400">
          Therapeuten-Bereich
        </p>
        <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-zinc-100 sm:text-4xl">
          Hallo, {user?.name?.split(" ")[0]}
        </h1>
        <p className="mt-2 text-base text-zinc-400">
          {patients.length} {patients.length === 1 ? "Patient" : "Patienten"} in deiner Betreuung
        </p>
      </div>

      {unassigned.length > 0 && (
        <section data-testid="unassigned-patients-section">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-zinc-200">
            <UserPlus className="h-5 w-5 text-primary" />
            Neue Patienten ohne Therapeut
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {unassigned.map((p) => (
              <div
                key={p.user_id}
                className="rounded-xl border border-dashed border-primary/40 bg-accent/40 p-5"
                data-testid={`unassigned-patient-card-${p.user_id}`}
              >
                <p className="font-semibold text-zinc-100">{p.name}</p>
                <p className="text-sm text-zinc-400">{p.email}</p>
                <Button
                  size="sm"
                  className="mt-4 bg-primary hover:bg-primary/90"
                  onClick={() => claim(p.user_id)}
                  data-testid={`claim-patient-button-${p.user_id}`}
                >
                  Patient übernehmen
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section data-testid="my-patients-section">
        <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-zinc-200">
          <Users className="h-5 w-5 text-primary" />
          Meine Patienten
        </h2>
        {patients.length === 0 ? (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-10 text-center" data-testid="no-patients-message">
            <ClipboardList className="mx-auto mb-3 h-10 w-10 text-zinc-600" />
            <p className="text-zinc-400">
              Noch keine Patienten. Sobald sich Patienten registrieren, kannst du sie hier
              übernehmen und Trainingspläne erstellen.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {patients.map((p) => (
              <button
                key={p.user_id}
                onClick={() => navigate(`/therapist/patients/${p.user_id}`)}
                className="group rounded-xl border border-zinc-800 bg-zinc-900 p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                data-testid={`patient-card-${p.user_id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-zinc-100">{p.name}</p>
                    <p className="text-sm text-zinc-400">{p.email}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-zinc-600 transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                </div>
                <div className="mt-4">
                  {p.active_plan ? (
                    <Badge className="bg-accent text-primary hover:bg-accent" data-testid={`patient-plan-badge-${p.user_id}`}>
                      {p.active_plan.title} · {p.active_plan.exercise_count} Übungen
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-zinc-400" data-testid={`patient-no-plan-badge-${p.user_id}`}>
                      Kein aktiver Plan
                    </Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
